import React from 'react';
import { CartesianGrid, Legend, Line, LineChart, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { Gauge, Target } from 'lucide-react';
import { Badge } from '../ui/badge';
import { fmtDate } from './constants';

export const SCHEDULE_STATUS = {
  en_tiempo: { label: 'En tiempo', cls: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300' },
  en_riesgo: { label: 'En riesgo', cls: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300' },
  atrasado: { label: 'Atrasado', cls: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300' },
  sin_datos: { label: 'Sin datos', cls: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300' },
};
const today = new Date().toISOString().slice(0, 10);
const tick = { fill: '#94a3b8', fontSize: 11 };
const ts = (d) => new Date(`${String(d).slice(0, 10)}T12:00:00`).getTime();
const short = (t) => new Date(t).toLocaleDateString('es', { day: '2-digit', month: 'short', year: '2-digit' });
const compact = (v) => Number(v).toLocaleString('es', { notation: 'compact', maximumFractionDigits: 1 });
const ORDER = ['Plan', 'Actual', 'Forecast'];

const Chart = ({ data, unit, testid, yDomain }) => (
  <div className="h-64" data-testid={testid}>
    <ResponsiveContainer width="100%" height="100%">
      <LineChart data={data.map((p) => ({ ...p, t: ts(p.date) }))}>
        <CartesianGrid strokeDasharray="3 3" stroke="#334155" opacity={0.4} />
        <XAxis dataKey="t" type="number" scale="time" domain={['dataMin', 'dataMax']} tickFormatter={short} tick={tick} minTickGap={24} />
        <YAxis tick={tick} unit={unit} domain={yDomain || ['auto', 'auto']} width={70} tickFormatter={unit ? undefined : compact} />
        <Tooltip labelFormatter={(t) => fmtDate(new Date(t).toISOString())} formatter={(v) => Number(v).toLocaleString('es', { maximumFractionDigits: 2 })} contentStyle={{ background: '#0f172a', border: '1px solid #334155', color: '#e2e8f0' }} />
        <Legend itemSorter={(i) => ORDER.indexOf(i.value)} />
        <ReferenceLine x={ts(today)} stroke="#f87171" strokeDasharray="4 4" label={{ value: 'Hoy', fill: '#f87171', fontSize: 11 }} />
        <Line dataKey="plan" name="Plan" stroke="#f59e0b" strokeWidth={2} dot={false} connectNulls />
        <Line dataKey="actual" name="Actual" stroke="#38bdf8" strokeWidth={2.5} connectNulls />
        <Line dataKey="forecast" name="Forecast" stroke="#a78bfa" strokeWidth={2} strokeDasharray="6 4" dot={false} connectNulls />
      </LineChart>
    </ResponsiveContainer>
  </div>
);

const Metric = ({ label, value, testid }) => (
  <div className="rounded-xl border border-slate-200 p-3 dark:border-slate-700" data-testid={testid}>
    <p className="text-[11px] uppercase tracking-wider text-slate-500">{label}</p><p className="mt-1 text-lg font-bold">{value}</p>
  </div>
);

const KpiForecast = ({ k }) => (
  <div className="rounded-xl border border-slate-200 p-4 dark:border-slate-700" data-testid={`kpi-forecast-${k.prediction_id}`}>
    <div className="mb-2 flex flex-wrap items-center gap-2">
      <Target className="h-4 w-4 text-amber-500" /><p className="font-semibold">{k.kpi_name}</p>
      {k.method === 'datos_insuficientes' ? <Badge variant="outline" data-testid={`kpi-forecast-status-${k.prediction_id}`}>Datos insuficientes para tendencia ({k.points} medición)</Badge>
        : <Badge className={k.will_meet ? SCHEDULE_STATUS.en_tiempo.cls : k.will_meet === false ? SCHEDULE_STATUS.atrasado.cls : SCHEDULE_STATUS.sin_datos.cls} data-testid={`kpi-forecast-status-${k.prediction_id}`}>
          {k.will_meet ? 'La tendencia alcanza la meta' : k.will_meet === false ? 'La tendencia NO alcanza la meta' : 'Sin fecha objetivo'}</Badge>}
      {k.forecast_at_target != null && <span className="text-xs text-slate-500">Forecast a la fecha objetivo: <b>{Number(k.forecast_at_target).toLocaleString('es', { maximumFractionDigits: 2 })}</b></span>}
      {k.fit && <span className="text-xs text-slate-500">Tendencia lineal · {k.points} puntos · R² {k.fit.r2}</span>}
    </div>
    <Chart data={k.series} testid={`kpi-forecast-chart-${k.prediction_id}`} />
  </div>
);

export const PvAvFPanel = ({ engines }) => {
  const s = engines.schedule;
  return (
    <div className="space-y-6" data-testid="pvavf-panel">
      <section className="space-y-3">
        <div className="flex flex-wrap items-center gap-2"><Gauge className="h-5 w-5 text-sky-500" /><h4 className="font-semibold">Avance del proyecto</h4>
          <Badge className={SCHEDULE_STATUS[s.status].cls} data-testid="pvavf-status">{SCHEDULE_STATUS[s.status].label}</Badge></div>
        <div className="grid gap-3 sm:grid-cols-5">
          <Metric label="Plan a hoy" value={`${s.planned_pct}%`} testid="pvavf-plan" />
          <Metric label="Actual" value={`${s.actual_pct}%`} testid="pvavf-actual" />
          <Metric label="SPI" value={s.spi ?? '—'} testid="pvavf-spi" />
          <Metric label="Fin plan de actividades" value={fmtDate(s.planned_finish)} />
          <Metric label="Fin proyectado" value={`${fmtDate(s.forecast_finish)}${s.forecast_delay_days ? ` (+${s.forecast_delay_days} d)` : ''}`} testid="pvavf-forecast-finish" />
        </div>
        <p className="text-xs text-slate-500">SPI = avance real ÷ avance planificado a hoy (ponderado por duración). El forecast extiende el plazo al ritmo actual. La curva "Actual" se construye con el avance registrado cada día que se actualiza una actividad.</p>
        <Chart data={s.series} unit="%" yDomain={[0, 100]} testid="pvavf-project-chart" />
      </section>
      <section className="space-y-3">
        <h4 className="font-semibold">KPIs comprometidos</h4>
        {engines.kpis.map((k) => <KpiForecast key={k.prediction_id} k={k} />)}
        {!engines.kpis.length && <p className="text-sm text-slate-400">Agrega predicciones en "Predicción vs Realidad" para proyectar sus KPIs.</p>}
      </section>
    </div>
  );
};
